import { useState } from "react";
import {
  AlertTriangle,
  Activity,
  Pill,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Info,
  ShieldAlert,
} from "lucide-react";

/**
 * Carte d'alerte rupture avec variant par criticité.
 *
 * 4 variants visuels selon meta.criticality :
 * - critical : bordeaux foncé en évidence (MITM sans alternative)
 * - high     : bordeaux clair, accentué (médicament habituellement vendu)
 * - moderate : ivoire avec bordure navy (alternative disponible)
 * - info     : neutre discret (rupture non habituelle)
 *
 * Charte Cabinet stricte. Aucun rouge vif, aucun vert. Aucun emoji.
 * Expansion inline (pas de modale) pour préserver le contexte de lecture.
 */

const VARIANTS = {
  critical: {
    bg: "bg-cabinet-bordeaux text-cabinet-cream",
    border: "border-cabinet-bordeaux",
    badge: "bg-cabinet-cream/15 text-cabinet-cream border-cabinet-cream/20",
    accent: "text-cabinet-cream",
    icon: AlertTriangle,
    iconColor: "text-cabinet-gold",
    label: "Critique",
    description: "MITM en rupture, aucune alternative documentée",
  },
  high: {
    bg: "bg-white",
    border: "border-cabinet-bordeaux/40",
    badge: "bg-cabinet-bordeaux/10 text-cabinet-bordeaux border-cabinet-bordeaux/20",
    accent: "text-cabinet-bordeaux",
    icon: ShieldAlert,
    iconColor: "text-cabinet-bordeaux",
    label: "Élevée",
    description: "Vous délivrez habituellement ce médicament",
  },
  moderate: {
    bg: "bg-cabinet-cream",
    border: "border-cabinet-navy/20",
    badge: "bg-cabinet-navy/10 text-cabinet-navy border-cabinet-navy/15",
    accent: "text-cabinet-navy",
    icon: Activity,
    iconColor: "text-cabinet-navy",
    label: "Modérée",
    description: "Une alternative thérapeutique est disponible",
  },
  info: {
    bg: "bg-white",
    border: "border-cabinet-mute-2/40",
    badge: "bg-cabinet-mute-1/10 text-cabinet-mute-1 border-cabinet-mute-2/40",
    accent: "text-cabinet-ink",
    icon: Info,
    iconColor: "text-cabinet-mute-1",
    label: "Information",
    description: "Rupture non habituelle pour votre activité",
  },
};


// ── Helpers ────────────────────────────────────────────────────────────────────


function formatDateFr(isoDate) {
  if (!isoDate) return null;
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    const datePart = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
    return datePart;
  } catch {
    return isoDate;
  }
}


function MatchingConfidenceBadge({ criticality, matchingStrategy }) {
  // Indicateur de confiance affiché uniquement pour les ruptures matchées HIGH
  // (basées sur les libellés tickets). Critical/moderate/info ne nécessitent
  // pas d'indication de confiance puisqu'elles ne dépendent pas du matching.
  if (criticality !== "high") return null;

  const isFuzzy = matchingStrategy === "libelle_fuzzy";
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-cabinet-mute-1 italic"
      title={
        isFuzzy
          ? "Correspondance trouvée par rapprochement de libellé. Vérifiez avant action."
          : "Correspondance certaine via code-barre CIP13."
      }
      data-testid="matching-confidence-badge"
    >
      <Info size={11} strokeWidth={1.8} />
      {isFuzzy ? "Correspondance probable" : "Correspondance exacte"}
    </span>
  );
}


// ── Composant principal ───────────────────────────────────────────────────────


export function RuptureCard({ rupture, matchingStrategy = null }) {
  const [expanded, setExpanded] = useState(false);
  const variant = VARIANTS[rupture.criticality] || VARIANTS.info;
  const Icon = variant.icon;
  const isCritical = rupture.criticality === "critical";

  const altCount = (rupture.alternatives || []).length;
  const statutLabel = {
    rupture: "Rupture de stock",
    tension: "Tension d'approvisionnement",
    arret: "Arrêt de commercialisation",
    remise_a_disposition: "Remise à disposition",
  }[rupture.statut] || rupture.statut;

  return (
    <article
      className={`${variant.bg} ${variant.border} border rounded-lg overflow-hidden transition-colors`}
      data-testid={`rupture-card-${rupture.criticality}`}
      data-rupture-id={rupture.id}
    >
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full text-left px-6 py-5 flex items-start gap-4 group"
        data-testid="rupture-card-toggle"
        aria-expanded={expanded}
      >
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            isCritical ? "bg-cabinet-cream/10" : "bg-cabinet-bordeaux/8"
          }`}
        >
          <Icon size={18} className={variant.iconColor} strokeWidth={1.7} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span
              className={`text-[10.5px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded border ${variant.badge}`}
            >
              {variant.label}
            </span>
            <span
              className={`text-[11.5px] tracking-wide ${
                isCritical ? "text-cabinet-cream/75" : "text-cabinet-mute-1"
              }`}
            >
              {statutLabel}
            </span>
            <MatchingConfidenceBadge
              criticality={rupture.criticality}
              matchingStrategy={matchingStrategy}
            />
          </div>

          <h3
            className={`font-playfair text-lg lg:text-xl font-medium leading-snug tracking-tight ${
              isCritical ? "text-cabinet-cream" : "text-cabinet-ink"
            }`}
          >
            {rupture.denomination}
          </h3>

          <p
            className={`mt-1.5 text-[13px] tracking-wide ${
              isCritical ? "text-cabinet-cream/80" : "text-cabinet-navy"
            }`}
          >
            {variant.description}
            {altCount > 0 && (
              <span className="ml-2">
                · {altCount} alternative{altCount > 1 ? "s" : ""} documentée{altCount > 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>

        <span
          className={`flex-shrink-0 mt-1 transition-transform ${expanded ? "rotate-180" : ""} ${
            isCritical ? "text-cabinet-cream/70" : "text-cabinet-mute-1"
          }`}
        >
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {expanded && (
        <div
          className={`px-6 pb-6 pt-2 border-t space-y-5 ${
            isCritical
              ? "border-cabinet-cream/15 text-cabinet-cream/90"
              : "border-cabinet-mute-2/40 text-cabinet-ink"
          }`}
          data-testid="rupture-card-detail"
        >
          {/* Métadonnées */}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
            <Field
              label="Code CIP13"
              value={rupture.cip13 || "Non communiqué"}
              isCritical={isCritical}
            />
            <Field
              label="Code CIS"
              value={rupture.cis_code || "Non communiqué"}
              isCritical={isCritical}
            />
            <Field
              label="Date de début"
              value={formatDateFr(rupture.date_debut) || "Non précisée"}
              isCritical={isCritical}
            />
            <Field
              label="Fin estimée"
              value={formatDateFr(rupture.date_fin_estimee) || "Non précisée"}
              isCritical={isCritical}
            />
            <Field
              label="Sources"
              value={(rupture.sources || []).join(", ").toUpperCase() || "—"}
              isCritical={isCritical}
            />
            <Field
              label="Statut MITM"
              value={rupture.is_mitm ? "Oui — médicament d'intérêt thérapeutique majeur" : "Non"}
              isCritical={isCritical}
            />
          </dl>

          {/* Alternatives */}
          {altCount > 0 && (
            <div>
              <h4
                className={`text-[11px] uppercase tracking-[0.18em] font-semibold mb-2.5 ${
                  isCritical ? "text-cabinet-cream/70" : "text-cabinet-mute-1"
                }`}
              >
                Alternatives thérapeutiques
              </h4>
              <ul className="space-y-2">
                {rupture.alternatives.map((alt, i) => (
                  <AlternativeItem key={i} alt={alt} isCritical={isCritical} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </article>
  );
}


function Field({ label, value, isCritical }) {
  return (
    <div>
      <dt
        className={`text-[10.5px] uppercase tracking-[0.18em] mb-0.5 ${
          isCritical ? "text-cabinet-cream/55" : "text-cabinet-mute-1"
        }`}
      >
        {label}
      </dt>
      <dd
        className={`text-[13px] tracking-wide ${
          isCritical ? "text-cabinet-cream" : "text-cabinet-ink"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}


function AlternativeItem({ alt, isCritical }) {
  const dispo = alt.disponibilite;
  const isAvailable = dispo === "disponible";
  const isUnavailable = dispo === "indisponible";
  return (
    <li
      className={`flex items-start gap-2.5 text-[13.5px] leading-relaxed ${
        isCritical ? "text-cabinet-cream/90" : "text-cabinet-ink"
      }`}
      data-testid="rupture-alternative-item"
    >
      <span className="flex-shrink-0 mt-0.5">
        {isAvailable ? (
          <CheckCircle2
            size={14}
            className={isCritical ? "text-cabinet-gold" : "text-cabinet-forest"}
            strokeWidth={1.8}
          />
        ) : isUnavailable ? (
          <XCircle
            size={14}
            className={isCritical ? "text-cabinet-cream/50" : "text-cabinet-mute-1"}
            strokeWidth={1.8}
          />
        ) : (
          <Pill
            size={14}
            className={isCritical ? "text-cabinet-cream/65" : "text-cabinet-mute-1"}
            strokeWidth={1.8}
          />
        )}
      </span>
      <span className="flex-1">
        <span className="font-medium">{alt.denomination}</span>
        {alt.cip13 && (
          <span
            className={`ml-2 text-[11.5px] tracking-wide ${
              isCritical ? "text-cabinet-cream/55" : "text-cabinet-mute-1"
            }`}
          >
            CIP {alt.cip13}
          </span>
        )}
        {dispo && (
          <span
            className={`block text-[11.5px] tracking-wide mt-0.5 ${
              isCritical ? "text-cabinet-cream/60" : "text-cabinet-mute-1"
            }`}
          >
            {isAvailable ? "Disponible" : isUnavailable ? "Indisponible actuellement" : "Disponibilité à vérifier"}
          </span>
        )}
      </span>
    </li>
  );
}


// ── Wrappers nommés exigés par la spec (variants par criticité) ────────────────

export function CriticalAlertCard(props) {
  return <RuptureCard {...props} rupture={{ ...props.rupture, criticality: "critical" }} />;
}
export function HighAlertCard(props) {
  return <RuptureCard {...props} rupture={{ ...props.rupture, criticality: "high" }} />;
}
export function ModerateAlertCard(props) {
  return <RuptureCard {...props} rupture={{ ...props.rupture, criticality: "moderate" }} />;
}
export function InfoAlertCard(props) {
  return <RuptureCard {...props} rupture={{ ...props.rupture, criticality: "info" }} />;
}

export { formatDateFr };
